/**
 * VariantManager Module
 * Handles variant switching with smooth crossfade transitions
 */

import type { VariantConfig } from '../types';

export interface VariantManagerConfig {
  variants: VariantConfig[];
  initialVariant?: string;
  transitionDuration: number; // ms
  onVariantChange: (variant: VariantConfig, previousVariant: VariantConfig | null) => void;
  onTransitionStart?: (from: string | null, to: string) => void;
  onTransitionEnd?: (variant: string) => void;
}

export class VariantManager {
  private config: VariantManagerConfig;
  private currentVariant: VariantConfig | null = null;
  private isTransitioning: boolean = false;
  private variantsMap: Map<string, VariantConfig>;

  constructor(config: VariantManagerConfig) {
    this.config = config;
    this.variantsMap = new Map();

    // Build variants lookup map
    for (const variant of config.variants) {
      this.variantsMap.set(variant.id, variant);
    }

    // Set initial variant
    if (config.initialVariant && this.variantsMap.has(config.initialVariant)) {
      this.currentVariant = this.variantsMap.get(config.initialVariant)!;
    } else if (config.variants.length > 0) {
      this.currentVariant = config.variants[0];
    }
  }

  /**
   * Get current variant
   */
  getCurrentVariant(): VariantConfig | null {
    return this.currentVariant;
  }

  /**
   * Get all variants
   */
  getAllVariants(): VariantConfig[] {
    return this.config.variants;
  }

  /**
   * Get variant by ID
   */
  getVariant(id: string): VariantConfig | null {
    return this.variantsMap.get(id) ?? null;
  }

  /**
   * Get variant by color and custom IDs
   */
  getVariantByColorAndCustom(colorId: string, customId: string): VariantConfig | null {
    const id = `${colorId}_${customId}`;
    return this.variantsMap.get(id) ?? null;
  }

  /**
   * Get current color ID
   */
  getCurrentColorId(): string | null {
    if (!this.currentVariant) return null;
    return this.currentVariant.colorId;
  }

  /**
   * Get current custom ID
   */
  getCurrentCustomId(): string | null {
    if (!this.currentVariant) return null;
    return this.currentVariant.customId;
  }

  /**
   * Check if a variant exists
   */
  hasVariant(id: string): boolean {
    return this.variantsMap.has(id);
  }

  /**
   * Switch to a different variant
   */
  async switchVariant(variantId: string): Promise<boolean> {
    // Validate variant exists
    const newVariant = this.variantsMap.get(variantId);
    if (!newVariant) {
      return false;
    }

    // Skip if already current
    if (this.currentVariant?.id === variantId) {
      return true;
    }

    // Skip if already transitioning
    if (this.isTransitioning) {
      return false;
    }

    const previousVariant = this.currentVariant;
    this.isTransitioning = true;

    // Notify transition start
    this.config.onTransitionStart?.(previousVariant?.id ?? null, variantId);

    // Update current variant
    this.currentVariant = newVariant;

    // Notify variant change (this triggers loading and rendering)
    this.config.onVariantChange(newVariant, previousVariant);

    // Wait for transition duration
    await this.delay(this.config.transitionDuration);

    this.isTransitioning = false;

    // Notify transition end
    this.config.onTransitionEnd?.(variantId);

    return true;
  }

  /**
   * Check if currently transitioning
   */
  isInTransition(): boolean {
    return this.isTransitioning;
  }

  /**
   * Get transition duration
   */
  getTransitionDuration(): number {
    return this.config.transitionDuration;
  }

  /**
   * Update transition duration
   */
  setTransitionDuration(duration: number): void {
    this.config.transitionDuration = duration;
  }

  /**
   * Get index of current variant
   */
  getCurrentIndex(): number {
    if (!this.currentVariant) return -1;
    return this.config.variants.findIndex((v) => v.id === this.currentVariant!.id);
  }

  /**
   * Switch to next variant
   */
  async nextVariant(): Promise<boolean> {
    const currentIndex = this.getCurrentIndex();
    const nextIndex = (currentIndex + 1) % this.config.variants.length;
    return this.switchVariant(this.config.variants[nextIndex].id);
  }

  /**
   * Switch to previous variant
   */
  async previousVariant(): Promise<boolean> {
    const currentIndex = this.getCurrentIndex();
    const prevIndex = currentIndex <= 0 ? this.config.variants.length - 1 : currentIndex - 1;
    return this.switchVariant(this.config.variants[prevIndex].id);
  }

  /**
   * Helper delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
